/**
 * Module summary:
 * This file manages SMS-based multi-factor authentication. It wraps Firebase
 * Phone Auth and Multi-Factor APIs with clearer app-specific behavior and keeps
 * a mock fallback for local development when Firebase is disabled.
 */

import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  type MultiFactorResolver
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebaseConfig';

const USE_FIREBASE = (process.env.REACT_APP_DATA_PROVIDER || 'firebase') === 'firebase';
const LEGACY_MFA_ENROLLED_KEY = 'macha.mfaEnrolled';

let recaptchaVerifier: RecaptchaVerifier | null = null;

function getMockCurrentUserId(): string | null {
  try {
    const sessionJson = localStorage.getItem('mockAuthSession');
    if (!sessionJson) return null;
    const session = JSON.parse(sessionJson) as { user?: { id?: string } };
    // Read the mock user id from the stored session so local MFA flags stay
    // tied to the same simulated account.
    return session.user?.id || null;
  } catch {
    return null;
  }
}

function getMfaEnrollmentKey(userId: string): string {
  return `macha.mfaEnrolled.${userId}`;
}

/**
 * Initialize a RecaptchaVerifier on a given DOM element.
 * Must be called before enrollment or sign-in verification.
 */
export function initRecaptcha(elementId: string): RecaptchaVerifier | null {
  if (!USE_FIREBASE) return null;
  // Destroy any existing verifier so Firebase doesn't complain about a stale
  // reference to an element that may have been removed from the DOM.
  if (recaptchaVerifier) {
    try { recaptchaVerifier.clear(); } catch (e) { console.warn('RecaptchaVerifier.clear() failed:', e); }
    recaptchaVerifier = null;
  }
  // Firebase Phone Auth requires a RecaptchaVerifier so the browser proves this
  // request came from a real user interaction instead of an automated script.
  recaptchaVerifier = new RecaptchaVerifier(getFirebaseAuth(), elementId, { size: 'invisible' });
  return recaptchaVerifier;
}

/**
 * Check if current user has enrolled MFA.
 */
export function isMfaEnrolled(): boolean {
  if (!USE_FIREBASE) {
    const userId = getMockCurrentUserId();
    // Prefer the user-specific key so multiple local users do not share one MFA flag.
    if (userId && localStorage.getItem(getMfaEnrollmentKey(userId)) === 'true') return true;
    return localStorage.getItem(LEGACY_MFA_ENROLLED_KEY) === 'true';
  }
  const user = getFirebaseAuth().currentUser;
  if (!user) return false;
  // Firebase exposes enrolled second factors on the authenticated user object.
  return multiFactor(user).enrolledFactors.length > 0;
}

/**
 * Start MFA enrollment: sends verification SMS to the phone number.
 * Returns a verificationId needed to complete enrollment.
 *
 * Firebase requires the user's email to be verified before enrolling
 * second factors. If the email is not verified, this function throws
 * a descriptive error instead of the raw Firebase auth/unverified-email error.
 */
export async function startMfaEnrollment(phoneNumber: string): Promise<string> {
  if (!USE_FIREBASE) {
    // Mock: simulate sending SMS
    await new Promise(r => setTimeout(r, 800));
    localStorage.setItem('macha.mfaPending', phoneNumber);
    return 'mock-verification-id';
  }

  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('No authenticated user');

  // Firebase MFA requires email verification before enrolling second factors.
  // Check proactively to provide a clear error message.
  if (!user.emailVerified) {
    throw new Error(
      'Please verify your email address before setting up multi-factor authentication. ' +
      'Check your inbox for a verification link, then try again.'
    );
  }

  if (!recaptchaVerifier) throw new Error('Recaptcha not initialized. Call initRecaptcha() first.');

  const session = await multiFactor(user).getSession();
  // Bind the SMS enrollment attempt to the current Firebase MFA session so the
  // returned verification code can only complete this enrollment flow.
  const phoneInfoOptions = { phoneNumber, session };
  const phoneAuthProvider = new PhoneAuthProvider(getFirebaseAuth());
  return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
}

/**
 * Complete MFA enrollment with the SMS code the user received.
 */
export async function completeMfaEnrollment(
  verificationId: string,
  smsCode: string,
  options?: { replaceExisting?: boolean; phoneNumber?: string }
): Promise<void> {
  if (!USE_FIREBASE) {
    await new Promise(r => setTimeout(r, 500));
    if (!/^\d{6}$/.test(smsCode)) throw new Error('Invalid verification code. Please enter a 6-digit code.');
    const userId = getMockCurrentUserId();
    if (userId) {
      localStorage.setItem(getMfaEnrollmentKey(userId), 'true');
      localStorage.removeItem(LEGACY_MFA_ENROLLED_KEY);
    } else {
      localStorage.setItem(LEGACY_MFA_ENROLLED_KEY, 'true');
    }
    localStorage.removeItem('macha.mfaPending');
    return;
  }

  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('No authenticated user');

  const existingFactors = [...multiFactor(user).enrolledFactors];
  const cred = PhoneAuthProvider.credential(verificationId, smsCode);
  // Convert the SMS code into a phone credential, then wrap it as the MFA
  // assertion Firebase expects for enrollment.
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  await multiFactor(user).enroll(assertion, 'Phone Number');

  if (options?.replaceExisting) {
    // Remove previous factors only after the new factor is enrolled so the user
    // is not left without MFA if something fails earlier.
    for (const factor of existingFactors) {
      await multiFactor(user).unenroll(factor);
    }
  }

  if (options?.phoneNumber) {
    try {
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { getFirebaseDb } = await import('../firebaseConfig');
      const db = getFirebaseDb();
      // Mirror the verified phone number into Firestore so profile screens can
      // show the same number the user just enrolled.
      await setDoc(doc(db, 'users', user.uid), {
        phone: options.phoneNumber,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (err) {
      console.error('Failed to sync phone number to profile:', err);
    }
  }
}

/**
 * Resolve a multi-factor sign-in challenge.
 * Called when login throws `auth/multi-factor-auth-required`.
 */
export async function startMfaSignIn(resolver: MultiFactorResolver | null): Promise<string> {
  if (!USE_FIREBASE) {
    // Mock: simulate sending SMS
    await new Promise(r => setTimeout(r, 800));
    return 'mock-signin-verification-id';
  }

  if (!resolver) throw new Error('MFA sign-in resolver is missing. Please sign in again to trigger the MFA challenge.');
  if (!recaptchaVerifier) throw new Error('Recaptcha not initialized. Call initRecaptcha() first.');

  const phoneInfoOptions = {
    // Reuse the factor hint and sign-in session Firebase returned when it paused the original login.
    multiFactorHint: resolver.hints[0],
    session: resolver.session
  };
  const phoneAuthProvider = new PhoneAuthProvider(getFirebaseAuth());
  return phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
}

/**
 * Complete multi-factor sign-in with the SMS code.
 */
export async function completeMfaSignIn(
  resolver: MultiFactorResolver | null,
  verificationId: string,
  smsCode: string
): Promise<void> {
  if (!USE_FIREBASE) {
    await new Promise(r => setTimeout(r, 500));
    if (!/^\d{6}$/.test(smsCode)) throw new Error('Invalid verification code. Please enter a 6-digit code.');
    return;
  }

  if (!resolver) throw new Error('Resolver is required for Firebase MFA.');
  const cred = PhoneAuthProvider.credential(verificationId, smsCode);
  const assertion = PhoneMultiFactorGenerator.assertion(cred);
  // Finish the paused Firebase sign-in by proving the user also controls the second factor.
  await resolver.resolveSignIn(assertion);
}

/**
 * Unenroll MFA (remove all enrolled factors).
 */
export async function unenrollMfa(): Promise<void> {
  if (!USE_FIREBASE) {
    const userId = getMockCurrentUserId();
    if (userId) {
      localStorage.removeItem(getMfaEnrollmentKey(userId));
    }
    localStorage.removeItem(LEGACY_MFA_ENROLLED_KEY);
    return;
  }

  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('No authenticated user');

  const factors = multiFactor(user).enrolledFactors;
  for (const factor of factors) {
    // Walk every enrolled factor so MFA is fully removed instead of partially disabled.
    await multiFactor(user).unenroll(factor);
  }
}
